import { observer } from 'mobx-react';
import {
  DateField,
  MultiSelectField,
  NumberFormat,
  SelectField,
  TextField,
} from '@roc/ui';
import { formatDate, isNotBlank, parseDate } from '@roc/feature-utils';
import {
  entityTypeSelectOptions,
  fieldTypeOf,
  stateSelectOptions,
  statesToList,
  yesNoSelectOptions,
} from './entityReviewFields';
import { EntityAddressField } from './entityAddressField';
import { EntityBorrowersField } from './entityBorrowersField';
import { EntityFieldProps } from './entityFieldProps';

const selectOptionsFor = (type: string) => {
  if (type === 'state') return stateSelectOptions;
  if (type === 'entityType') return entityTypeSelectOptions;
  return yesNoSelectOptions;
};

export const EntityField = observer(({ store, item }: EntityFieldProps) => {
  const value = store.getExpected(item.itemName);
  const error = store.errors[item.itemName];
  const type = fieldTypeOf(item.itemName);
  const common = {
    label: item.itemDisplayName,
    name: item.itemName,
    testId: item.itemName,
    fullWidth: true,
    standaloneLabel: true,
  };

  switch (type) {
    case 'states': {
      const selected = statesToList(value).map(
        code =>
          stateSelectOptions.find(o => o.value === code) ?? {
            value: code,
            label: code,
          }
      );
      return (
        <MultiSelectField
          {...common}
          options={stateSelectOptions}
          value={selected}
          getOptionSelected={(option, selectedValue) =>
            option.value === selectedValue.value
          }
          onChange={(_event, next) =>
            store.onExpectedChange(
              item.itemName,
              (next ?? []).map(option => option.value)
            )
          }
          renderInput={params => (
            <TextField
              {...params}
              testId={item.itemName}
              variant="outlined"
              error={isNotBlank(error)}
              helperText={error}
            />
          )}
        />
      );
    }
    case 'address':
      return <EntityAddressField store={store} item={item} />;
    case 'borrowers':
      return <EntityBorrowersField store={store} item={item} />;
    case 'date':
      return (
        <DateField
          {...common}
          value={value ? parseDate(value) : null}
          onChange={next =>
            store.onExpectedChange(
              item.itemName,
              next ? formatDate(next, 'MM/dd/yyyy') : null
            )
          }
          format="MM/dd/yyyy"
          inputVariant="outlined"
          error={isNotBlank(error)}
          errorText={error}
        />
      );
    case 'ein':
      return (
        <NumberFormat
          {...common}
          customInput={TextField}
          variant="outlined"
          format="##-#######"
          mask="_"
          value={value ?? ''}
          onChange={e => store.onExpectedChange(item.itemName, e.target.value)}
          error={isNotBlank(error)}
          helperText={error}
        />
      );
    case 'state':
    case 'entityType':
    case 'yesNo':
      return (
        <SelectField
          {...common}
          variant="outlined"
          value={value ?? ''}
          onChange={next => store.onExpectedChange(item.itemName, next)}
          options={selectOptionsFor(type)}
          error={isNotBlank(error)}
          errorText={error}
        />
      );
    default:
      return (
        <TextField
          {...common}
          variant="outlined"
          value={value ?? ''}
          onChange={e => store.onExpectedChange(item.itemName, e.target.value)}
          error={isNotBlank(error)}
          helperText={error}
        />
      );
  }
});
